import clsx from 'clsx'
import { copySourceLabel } from '../utils/copy-content'
import TableMatchText from './TableMatchText'
import { Fragment } from 'react'
import TableReferenceLink from './TableReferenceLink'
import TableTag from './TableTag'
import type { CellReference, CellSegment, ColumnTag } from '../schemas'

/** Render saved reference occurrences in reading order, independent of answer wording. */
export default function CellEvidence({
  segments,
  references,
  matterId,
  tableId,
  highlightOnHover = false,
  tags,
}: {
  segments: CellSegment[]
  references: CellReference[]
  matterId?: string
  tableId?: string
  highlightOnHover?: boolean
  tags?: ColumnTag[]
}) {
  const byId = new Map(references.map((reference) => [reference.id, reference]))
  const parts = segments.map((segment, index) => ({
    segment,
    start:
      segments.slice(0, index).reduce((sum, part) => sum + Array.from(part.text).length, 0) +
      (tags ? 2 * segments.slice(0, index).filter((part) => part.text).length : 0),
  }))
  const numbers = new Map<string, number>()
  for (const { segment } of parts) {
    for (const id of segment.reference_ids) {
      if (!numbers.has(id)) numbers.set(id, numbers.size + 1)
    }
  }
  return (
    <span
      className={clsx(
        'whitespace-pre-wrap break-words rounded',
        tags && 'flex flex-wrap items-center gap-1.5',
        highlightOnHover && '[&:hover:not(:has(a:hover))]:bg-brand-neutral-100'
      )}
    >
      {parts.map(({ segment, start }, index) => (
        <Fragment key={index}>
          {tags && segment.text && index > 0 && <span className="sr-only">{parts.slice(0, index).some((part) => part.segment.text) ? ', ' : ' '}</span>}
          {tags?.some((tag) => tag.label === segment.text) ? (
            <span className="inline-flex min-w-0 max-w-full [&>span]:inline-flex">
              <TableTag tag={tags.find((tag) => tag.label === segment.text)!}>
                <TableMatchText text={segment.text} start={start} />
              </TableTag>
            </span>
          ) : (
            <TableMatchText text={segment.text} start={start} />
          )}
          {segment.reference_ids.map((id) => {
            const reference = byId.get(id)
            if (!reference || !matterId) return null
            return (
              <span
                key={id}
                className={tags ? 'inline-flex' : undefined}
                data-table-reference-id={id}
                data-table-reference-source={copySourceLabel(reference, matterId, tableId)}
              >
                <TableReferenceLink
                  reference={reference}
                  matterId={matterId}
                  className={clsx(
                    'px-1 pt-0 pb-[1px] rounded text-xs border inline-flex items-center min-h-[18px] text-brand-neutral-800 bg-brand-neutral-200 border-brand-neutral-300 hover:bg-brand-neutral-300 transition-colors',
                    !tags && 'mx-0.5'
                  )}
                >
                  {numbers.get(id)}
                </TableReferenceLink>
              </span>
            )
          })}
        </Fragment>
      ))}
    </span>
  )
}
